import { FormConfig } from 'components/common/form/FormConfig';
import React, { useCallback, useEffect, useMemo } from 'react';
import i18n from 'i18n';
import { Form } from 'components/common/form/Form';
import { validateEmpty, validateMinimum } from 'utils/ValidateUtils';
import { Formik, FormikProps } from 'formik';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { PmpFormType, PmpType } from 'core/pmp/Pmp';
import moment from 'moment';
import {
  camelCase,
  defaultTo,
  get,
  isEmpty,
  isUndefined,
  omitBy
} from 'lodash';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { useCoreContext } from 'contexts/coreContext';
import { Trans } from 'react-i18next';
import { usePmpDurationValidator } from './PmpDurationValidator';
import styles from './pmp.module.scss';
import { formatPrice, getCurrencyLabel } from 'helper/CurrencyHelper';
import { Dayparts, getInitDaypart } from 'components/Dayparts/Dayparts';
import { Dayparts as DaypartsType } from 'core/dayparts/Dayparts';

export type PmpFormProps = {
  type: 'create' | 'edit';
  pmpFormData: PmpFormType;
  haveBoundCampaign: boolean;
  channelOptions: SelectOptions[];
  channelSpaceOptionsMap: { [channelId: number]: SelectOptions[] };
  channelAgencyOptionsMap: { [channelId: number]: SelectOptions[] };
  onSubmit: (pmp: PmpFormType) => void;
};

const pmpTypeOptions = createSelectOptionsFromEnum(
  PmpType,
  'pmp.pmpTypes.',
  [],
  camelCase
);

export const PmpForm: React.FC<PmpFormProps> = ({
  type,
  pmpFormData,
  haveBoundCampaign,
  channelOptions,
  channelSpaceOptionsMap,
  channelAgencyOptionsMap,
  onSubmit
}) => {
  const core = useCoreContext();
  const currency = get(core, 'accountManager.localeMeta.currency', 'NTD');

  const { loading, fetchPmpWithinDuration, validatePmpDuration } =
    usePmpDurationValidator();

  const initPmpStartTime = pmpFormData.startTime;
  const initPmpEndTime = pmpFormData.endTime;
  useEffect(() => {
    fetchPmpWithinDuration(initPmpStartTime, initPmpEndTime);
  }, [initPmpStartTime, initPmpEndTime, fetchPmpWithinDuration]);

  // 最快明天開始
  const minDate = useMemo(
    () => moment().startOf('day').add(1, 'day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  // 最多從明天算起 90 天內
  const maxDate = useMemo(
    () =>
      moment()
        .startOf('day')
        .add(90, 'days')
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  const validateForm = useCallback(
    (values: PmpFormType) => {
      const validateDuration = (startTime: string) => {
        const start = moment(startTime);
        if (start.isBefore(minDate)) {
          return i18n.t('pmpForm.errors.createBeforeMinDate');
        }
        return undefined;
      };

      const sponsorshipConflictData = validatePmpDuration(values);
      const spaceOptionsOfCurrentChannel = values.channelGroupId
        ? channelSpaceOptionsMap[values.channelGroupId]
        : [];
      const spaceError = sponsorshipConflictData
        ? sponsorshipConflictData.spaces.map((space) => {
            const target = spaceOptionsOfCurrentChannel.find(
              (option) => option.value === space
            );
            return target ? target.label : '';
          })
        : undefined;
      const spaceErrorString = spaceError
        ? `${i18n.t(
            'pmpForm.errors.sponsorshipConflict'
          )}:\n${spaceError.join('\n')}`
        : undefined;

      const durationError = get(sponsorshipConflictData, 'duration');
      const durationErrorString = durationError
        ? `${i18n.t(
            'pmpForm.errors.sponsorshipConflict'
          )}:\n${durationError.join('\n')}`
        : undefined;

      const daypartError = get(sponsorshipConflictData, 'dayPart');
      const daypartErrorString = daypartError
        ? `${i18n.t(
            'pmpForm.errors.sponsorshipConflict'
          )}: ${daypartError.join('、 ')}`
        : undefined;

      return omitBy(
        {
          name: validateEmpty(values.name),
          duration: validateDuration(values.startTime) || durationErrorString,
          pmpType: validateEmpty(values.pmpType),
          channelGroupId: validateEmpty(values.channelGroupId),
          budget: validateMinimum(values.budget, 1),
          adAgencyIds: omitBy(
            {
              0: validateEmpty(values.adAgencyIds[0])
            },
            isUndefined
          ),
          spaces: validateEmpty(values.spaces) || spaceErrorString,
          dayPart: values.dayPart?.enabled
            ? daypartErrorString
            : omitBy(
                {
                  enabled: daypartErrorString
                },
                isUndefined
              )
        },
        isEmpty
      );
    },
    [minDate, channelSpaceOptionsMap, validatePmpDuration]
  );

  const renderDaypart = useCallback((props) => <Dayparts {...props} />, []);

  const defaultDaypartValue: DaypartsType & { enabled: boolean } = useMemo(
    () => ({ ...getInitDaypart(), enabled: false }),
    []
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<PmpFormType>) => {
      const { values, setValues, setFieldValue, setFieldTouched } = formikProps;

      const spaceOptionsOfCurrentChannel = defaultTo(
        channelSpaceOptionsMap[values.channelGroupId],
        []
      );
      const agencyOptionsOfCurrentChannel = defaultTo(
        channelAgencyOptionsMap[values.channelGroupId],
        []
      );
      const showTimePicker = values.pmpType !== PmpType.BUYOUT;
      const onPmpTypeChange = (type: PmpType) => {
        setValues({
          ...values,
          pmpType: type,
          dayPart:
            type === PmpType.BUYOUT ? undefined : { ...defaultDaypartValue }
        });
      };

      const setRelatedFieldTouched = () => {
        setFieldTouched('duration', true);
        setFieldTouched('dayPart', true);
        setFieldTouched('dayPart.enabled', true);
        setFieldTouched('spaces', true);
      };

      const onDurationPopOverClosed = async () => {
        await fetchPmpWithinDuration(values.startTime, values.endTime);
        setRelatedFieldTouched();
      };

      const onDaypartSwitchChanged = (checked: boolean) => {
        !checked && setFieldValue('dayPart', { ...defaultDaypartValue });
      };

      const onChannelChanged = (channelId: number) => {
        setValues({
          ...values,
          channelGroupId: channelId,
          spaces: [],
          adAgencyIds: []
        });
      };

      const daypartEnabled = values.dayPart?.enabled;

      const start = moment(values.startTime);
      const end = moment(values.endTime);
      const days = end.diff(start, 'days') + 1;
      const budgetHint = i18n.t('pmpForm.hints.budget', {
        min: values.spaces
          ? formatPrice(currency, 3000 * values.spaces.length * days)
          : 0
      });

      const fieldsBuilder = new FormConfig.FieldsBuilder();
      fieldsBuilder.addFormikInput({
        label: i18n.t('pmp.labels.name'),
        name: 'name'
      });

      if (type === 'edit') {
        fieldsBuilder.addFormikLabel({
          label: i18n.t('pmp.labels.pmpType'),
          name: 'pmpType',
          formatter: (value: string) =>
            i18n.t(`pmp.pmpTypes.${camelCase(value)}`)
        });
      } else {
        fieldsBuilder.addFormikSelect({
          label: i18n.t('pmp.labels.pmpType'),
          name: 'pmpType',
          simpleValue: true,
          options: pmpTypeOptions,
          onChange: onPmpTypeChange
        });
      }

      fieldsBuilder.addFormikDateRangePicker({
        label: i18n.t('pmp.labels.duration'),
        name: 'duration',
        minDate,
        maxDate,
        showTimePicker,
        startDateFormikName: 'startTime',
        endDateFormikName: 'endTime',
        validateOnPopOverClose: true,
        onPopOverClosed: onDurationPopOverClosed,
        hint: (
          <Trans
            i18nKey='campaign.descriptions.campaignSchedulingDay'
            values={{ days }}
            components={{ 1: <span className='text-dark' /> }}
          />
        )
      });

      if (type === 'edit') {
        fieldsBuilder.addFormikLabel({
          label: i18n.t('pmp.labels.channelGroupId'),
          name: 'channelGroupId',
          formatter: (value: string) => i18n.t(`salesChannels.${value}`)
        });
      } else {
        fieldsBuilder.addFormikSelect({
          label: i18n.t('pmp.labels.channelGroupId'),
          name: 'channelGroupId',
          simpleValue: true,
          options: channelOptions,
          onChange: onChannelChanged
        });
      }

      fieldsBuilder.addFormikSwitch(
        {
          label: i18n.t('pmp.labels.dayparts'),
          name: 'dayPart.enabled',
          onChange: onDaypartSwitchChanged,
          hoverHint: daypartEnabled
            ? undefined
            : i18n.t('pmpForm.hints.dayparts')
        },
        values.pmpType === PmpType.BUYOUT
      );

      if (daypartEnabled) {
        fieldsBuilder.addFormikCustom({
          label: '',
          name: 'dayPart',
          onChange: setRelatedFieldTouched,
          render: renderDaypart
        });
      }

      fieldsBuilder.addFormikSelect({
        label: i18n.t('pmp.labels.spaces'),
        name: 'spaces',
        isMulti: true,
        simpleValue: true,
        menuPlacement: 'auto',
        options: spaceOptionsOfCurrentChannel,
        onChange: setRelatedFieldTouched
      });

      fieldsBuilder.addFormikInputGroup({
        label: i18n.t('pmp.labels.budget'),
        name: 'budget',
        type: 'number',
        prefix: getCurrencyLabel(currency),
        permanentHint: <span className={styles.hint}>{budgetHint}</span>
      });

      if (haveBoundCampaign) {
        fieldsBuilder.addFormikLabel({
          label: i18n.t('pmp.labels.agency'),
          name: 'adAgencyIds.0',
          formatter: (value: string) => i18n.t(`agency.name.${value}`)
        });
      } else {
        fieldsBuilder.addFormikSelect({
          label: i18n.t('pmp.labels.agency'),
          name: 'adAgencyIds.0',
          simpleValue: true,
          menuPlacement: 'auto',
          options: agencyOptionsOfCurrentChannel
        });
      }

      const formConfig = new FormConfig.Builder()
        .addSection(
          new FormConfig.SectionBuilder(fieldsBuilder.build())
            .withTitle(i18n.t('pmpSetupFlowPage.titles.formSection'))
            .build()
        )
        .build();

      const renderFormBtns = () => (
        <>
          <Form.SubmitButton>
            {i18n.t('common.buttons.completeAndCheck')}
          </Form.SubmitButton>
          <Form.CancelButton />
        </>
      );
      return (
        <Form
          formikProps={formikProps}
          formConfig={formConfig}
          renderFormBtns={renderFormBtns}
        />
      );
    },
    [
      type,
      currency,
      minDate,
      maxDate,
      haveBoundCampaign,
      channelOptions,
      channelSpaceOptionsMap,
      channelAgencyOptionsMap,
      defaultDaypartValue,
      renderDaypart,
      fetchPmpWithinDuration
    ]
  );

  if (!pmpFormData) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {loading && <LoadingIndicator />}
      <Formik
        initialValues={pmpFormData}
        onSubmit={onSubmit}
        validate={validateForm}
        validateOnBlur={false}
        enableReinitialize
      >
        {renderForm}
      </Formik>
    </>
  );
};
